import { API_URL } from './consts';

/**
 * Runtime OAuth provider identifiers shared with the backend contract.
 * Must be kept in sync with rust/base/src/oauth.rs.
 */
export const OAUTH_PROVIDER = {
  Google: 'google',
  Meta: 'meta',
} as const;

export type OAuthProvider = (typeof OAUTH_PROVIDER)[keyof typeof OAUTH_PROVIDER];

/** Narrows unknown values so callers can safely branch without casting. */
function isOAuthProvider(value: unknown): value is OAuthProvider {
  return (
    typeof value === 'string' && Object.values(OAUTH_PROVIDER).includes(value as OAuthProvider)
  );
}

/** Coerces URL query params into a typed provider; returns null for unsupported values. */
export function stringToProvider(providerStr?: string | null): OAuthProvider | null {
  return isOAuthProvider(providerStr) ? providerStr : null;
}

/** OAuth flow intents that must stay in sync with the backend enum. */
export enum OAuthIntent {
  Login = 'login',
  Register = 'register',
  LinkAccount = 'link_account',
}

/** Defaults to Login for missing/unrecognized intents so the OAuth flow never stalls. */
export function stringToIntent(intentStr?: string): OAuthIntent {
  if (!intentStr) return OAuthIntent.Login;

  // Check if the string matches any of our enum values
  const values = Object.values(OAuthIntent);
  for (const value of values) {
    if (value === intentStr) {
      return value as OAuthIntent;
    }
  }

  // Default to login for invalid intent strings
  return OAuthIntent.Login;
}

/**
 * Runtime OAuth callback status identifiers shared with the backend contract.
 * Must be kept in sync with rust/base/src/oauth.rs.
 */
export const OAuthResponseStatus = {
  // Success cases
  LoginSuccess: 'login_success',
  AccountLinked: 'account_linked',

  // Expected "error" cases that need special handling
  OAuthIdentityConflict: 'oauth_identity_conflict',
  NoExistingAccount: 'no_existing_account',
  SessionExpired: 'session_expired',
  NotLoggedIn: 'not_logged_in',
  ProviderMismatch: 'provider_mismatch',
  InvalidState: 'invalid_state',
  RegistrationRequired: 'registration_required',
} as const;

export type OAuthResponseStatus = (typeof OAuthResponseStatus)[keyof typeof OAuthResponseStatus];

interface OAuthUrlResponse {
  authorization_url: string;
}

interface CallbackRequest {
  provider: OAuthProvider;
  oauth_code: string;
  state: string;
  username?: string;
}

/**
 * OAuth identity information returned from the server.
 * Must match the OAuthIdentity struct in rust/base/src/kb/user/mod.rs.
 */
export interface OAuthIdentityInfo {
  provider: OAuthProvider;
  provider_user_id: string;
  email?: string;
  name?: string;
  picture?: string;
}

interface UserInfo {
  username: string;
  is_new_user: boolean;
  email?: string;
  name?: string;
}

/**
 * Response from the callback endpoint.
 * Must be kept in sync with the corresponding struct in rust/server/src/oauth.rs.
 */
interface CallbackResponse {
  status: OAuthResponseStatus;
  intent: string;
  provider: OAuthProvider;

  user_info?: UserInfo;
  oauth_identity?: OAuthIdentityInfo;
  invite_code?: string;

  rpcError?: boolean;
}

/** Untyped callback payload shape prior to runtime validation and normalization. */
interface RawCallbackResponse extends Omit<
  Partial<CallbackResponse>,
  'provider' | 'oauth_identity'
> {
  provider?: unknown;
  oauth_identity?: {
    provider?: unknown;
    provider_user_id?: unknown;
    email?: unknown;
    name?: unknown;
    picture?: unknown;
  };
}

/** Defensive narrowing — callback payloads can have arbitrary types per-field. */
function optionalString(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined;
}

/** Validates raw identity fields before trusting them as typed OAuthIdentityInfo. */
function normalizeOAuthIdentityInfo(
  rawIdentity: RawCallbackResponse['oauth_identity']
): OAuthIdentityInfo | undefined {
  if (!rawIdentity) {
    return undefined;
  }

  const provider = stringToProvider(optionalString(rawIdentity.provider));
  const provider_user_id = optionalString(rawIdentity.provider_user_id);

  if (!provider || !provider_user_id) {
    return undefined;
  }

  return {
    provider,
    provider_user_id,
    email: optionalString(rawIdentity.email),
    name: optionalString(rawIdentity.name),
    picture: optionalString(rawIdentity.picture),
  };
}

/** Kicks off the OAuth redirect — returns the provider's authorization URL or an rpcError flag. */
export async function initiateOAuthLogin(
  provider: OAuthProvider,
  intent: OAuthIntent,
  inviteCode?: string,
  returnUrl?: string
): Promise<{ url: string; rpcError?: boolean }> {
  // Construct URL with query parameters
  let url = `${API_URL}/oauth/prepare?provider=${provider}&intent=${intent}`;

  // Add optional parameters if provided
  if (inviteCode) {
    url += `&invite_code=${encodeURIComponent(inviteCode)}`;
  }

  if (returnUrl) {
    url += `&return_url=${encodeURIComponent(returnUrl)}`;
  }

  // Make the API request with all parameters
  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
  });

  let responseText = '';
  try {
    responseText = await response.clone().text();
  } catch (e) {
    // Ignore
  }

  const rpcError = !response.ok;

  if (rpcError) {
    console.error('OAuth prepare error:', responseText);
    return {
      url: '',
      rpcError: true,
    };
  }

  const data: OAuthUrlResponse = await response.json();

  // The server provides a complete authorization URL which already includes
  // the state parameter for session tracking through the OAuth flow
  return {
    url: data.authorization_url,
    rpcError: false,
  };
}

/**
 * Completes the OAuth exchange. Normalizes the raw backend response into a
 * typed CallbackResponse, handling edge cases like duplicate code redemption
 * and malformed identity payloads without throwing.
 */
export async function handleOAuthCallback(
  provider: OAuthProvider,
  oauth_code: string,
  state: string,
  options?: {
    username?: string;
  }
): Promise<CallbackResponse> {
  // The state parameter from the provider is our session state key

  const requestBody: CallbackRequest = {
    provider,
    oauth_code,
    state, // The state parameter is our session key
    username: options?.username,
  };

  const response = await fetch(`${API_URL}/oauth/callback`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify(requestBody),
  });

  // Try to parse the response as JSON, but handle non-JSON responses
  let responseData: CallbackResponse;
  let responseText = '';
  try {
    // First try to get the text to help with debugging
    responseText = await response.clone().text();

    // Then try to parse as JSON
    const rawResponseData = (await response.json()) as RawCallbackResponse;
    const responseProvider = stringToProvider(optionalString(rawResponseData.provider));
    const oauthIdentity = normalizeOAuthIdentityInfo(rawResponseData.oauth_identity);
    const hasInvalidOauthIdentity = !!rawResponseData.oauth_identity && !oauthIdentity;

    if (rawResponseData.provider !== undefined && !responseProvider) {
      console.error('Invalid OAuth provider in callback response:', rawResponseData.provider);
    }

    if (hasInvalidOauthIdentity) {
      console.error(
        'Invalid OAuth identity provider in callback response:',
        rawResponseData.oauth_identity?.provider
      );
    }

    responseData = {
      ...rawResponseData,
      provider: responseProvider ?? provider,
      oauth_identity: oauthIdentity,
      rpcError: Boolean(rawResponseData.rpcError),
    } as CallbackResponse;

    if (hasInvalidOauthIdentity) {
      responseData.rpcError = true;
    }
  } catch (error) {
    console.error('Failed to parse OAuth callback response as JSON:', error);
    console.error('Response text was:', responseText);

    // Instead of throwing, return a structured error response
    return {
      status: OAuthResponseStatus.InvalidState, // Use as generic error status
      intent: options?.username ? OAuthIntent.Register : OAuthIntent.Login,
      provider,
      rpcError: true,
    };
  }

  // Check for transport error or specific OAuth token exchange error
  const hasTransportError = !response.ok;
  const hasTokenExchangeError = responseText.includes('OAuth token exchange error');
  const hasInvalidCallbackContract = responseData.rpcError === true;
  const isCodeAlreadyUsed =
    response.status === 409 ||
    responseText.includes('OAuth code already used') ||
    responseText.includes('code has been used') ||
    responseText.includes('invalid_grant');

  // Special case: if it's just a "code already used" error and we got a successful response,
  // don't treat it as an RPC error - this is a normal condition from duplicate exchanges
  if (
    isCodeAlreadyUsed &&
    responseData.status &&
    responseData.intent &&
    !hasInvalidCallbackContract
  ) {
    console.warn('OAuth code already used, but we have valid response data. Proceeding normally.');
    responseData.rpcError = false;
  } else {
    // Otherwise, treat any transport or token exchange error as an RPC error
    responseData.rpcError =
      hasInvalidCallbackContract || hasTransportError || hasTokenExchangeError;
  }

  // Log errors for debugging
  if (responseData.rpcError) {
    console.error('OAuth callback error detected:');
    if (hasTransportError) {
      console.error(`- HTTP status: ${response.status}`);
    }
    if (hasTokenExchangeError) {
      console.error('- Token exchange error detected in response');
    }
    if (isCodeAlreadyUsed) {
      console.warn('- OAuth code already used (this may be due to duplicate requests)');
    }
    console.error('- Response text:', responseText);
  }

  return responseData;
}

// All state is managed server-side.  `state` is an id passed through
// the OAuth provider's state parameter and returned back to us in the callback.
